import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

function ComparisonView({ comparison, onClose }) {
  if (!comparison || !comparison.scenarios || comparison.scenarios.length < 2) {
    return (
      <div className="comparison-view empty">
        <p>Select at least two scenarios to compare</p>
        <button onClick={onClose}>Close</button>
      </div>
    );
  }

  // Generate data for reach comparison
  const reachData = comparison.scenarios.map(scenario => ({
    name: scenario.name,
    reach: scenario.results?.netReach || 0,
    frequency: scenario.results?.netFrequency || 0,
    budget: scenario.budget
  }));

  // Generate data for the curve comparison
  const generateCurveData = () => {
    // Find the maximum number of points in any curve
    const maxPoints = Math.max(...comparison.scenarios.map(
      scenario => scenario.results?.curvePoints?.length || 0
    ));
    
    if (maxPoints === 0) return [];
    
    // Create a data point for each budget percentage
    const dataPoints = [];
    for (let i = 0; i < maxPoints; i++) {
      const point = {
        budget: i * (100 / (maxPoints - 1)) // Evenly space budget percentages
      };
      
      // Add reach values for each scenario
      comparison.scenarios.forEach(scenario => {
        if (scenario.results?.curvePoints && scenario.results.curvePoints[i]) {
          point[`reach_${scenario.id}`] = scenario.results.curvePoints[i].reachPercentage;
        } else {
          point[`reach_${scenario.id}`] = null; // Use null for missing data points
        }
      });
      
      dataPoints.push(point);
    }
    
    return dataPoints;
  };

  const curveData = generateCurveData();

  // Colors for the scenarios
  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', '#00C49F'];

  return (
    <div className="comparison-view">
      <div className="comparison-header">
        <h2>Scenario Comparison</h2>
        <button onClick={onClose} className="close-btn">Close</button>
      </div>
      
      <div className="scenarios-overview">
        <h3>Scenarios Overview</h3>
        <div className="scenario-cards">
          {comparison.scenarios.map((scenario, index) => (
            <div key={scenario.id} className="scenario-card" style={{ borderColor: colors[index % colors.length] }}>
              <h4>{scenario.name}</h4>
              <div className="scenario-info">
                <p>Audience: {scenario.audienceName}</p>
                <p>Budget: ${scenario.budget.toLocaleString()}</p>
                {scenario.results && (
                  <>
                    <p>Reach: {scenario.results.netReach?.toFixed(2)}%</p>
                    <p>Frequency: {scenario.results.netFrequency?.toFixed(2)}x</p>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="comparison-charts">
        <div className="reach-comparison">
          <h3>Reach Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={reachData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" label={{ value: 'Reach (%)', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" label={{ value: 'Frequency (x)', angle: 90, position: 'insideRight' }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="reach" name="Reach %" fill="#8884d8" />
              <Bar yAxisId="right" dataKey="frequency" name="Frequency" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="curve-comparison">
          <h3>Reach Curve Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={curveData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="budget" 
                label={{ value: 'Budget (%)', position: 'insideBottom', offset: -5 }} 
              />
              <YAxis 
                label={{ value: 'Reach (%)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip formatter={(value) => value ? `${value.toFixed(2)}%` : 'N/A'} />
              <Legend />
              
              {comparison.scenarios.map((scenario, index) => (
                <Line
                  key={scenario.id}
                  type="monotone"
                  dataKey={`reach_${scenario.id}`}
                  name={scenario.name}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 8 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        <div className="efficiency-comparison">
          <h3>Budget Efficiency Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={reachData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis label={{ value: 'Cost per Reach Point ($)', angle: -90, position: 'insideLeft' }} />
              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
              <Legend />
              <Bar 
                dataKey={(data) => (data.budget / data.reach)}
                name="Cost per Reach Point" 
                fill="#ff8042" 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="channel-comparison">
        <h3>Channel Allocation Comparison</h3>
        <table className="comparison-table">
          <thead>
            <tr>
              <th>Channel</th>
              {comparison.scenarios.map(scenario => (
                <th key={scenario.id}>{scenario.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Get all unique channels across all scenarios */}
            {Array.from(new Set(
              comparison.scenarios.flatMap(scenario => 
                scenario.results?.channelPerformance?.map(cp => cp.channelName) || []
              )
            )).map(channelName => (
              <tr key={channelName}>
                <td>{channelName}</td>
                {comparison.scenarios.map(scenario => {
                  const channelData = scenario.results?.channelPerformance?.find(
                    cp => cp.channelName === channelName
                  );
                  return (
                    <td key={`${scenario.id}-${channelName}`}>
                      {channelData ? (
                        <div className="channel-data">
                          <div>Budget: ${(scenario.budget * channelData.budgetAllocation / 100).toLocaleString()}</div>
                          <div>Reach: {channelData.reach.toFixed(2)}%</div>
                          <div>Frequency: {channelData.frequency.toFixed(2)}x</div>
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ComparisonView;